import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { useHubStore, type HubView } from "@/components/hub/use-hub-store";
import { useShareableEntity } from "@/lib/api/shareable-entities";
import { findParticipantById } from "@/lib/hooks/conversations/use-conversation-participants";
import { getPreferredName } from "@/lib/redacted-display";
import { parseMessageData } from "@/lib/utils/parse-message-data";
import { useCallback, useMemo } from "react";

export interface EntitySharedData {
  entity_type: "note" | "moment" | "swap" | "story";
  entity_id: string;
  owner_id: string;
  preview_url?: string | null;
  second_preview_url?: string | null;
  swap_action?: "started" | "pending_mentor" | "pending_teen" | "completed";
}

export function useEntityShared(
  message: { data?: string },
  options?: { context?: ChatRenderContext },
) {
  const entityData = useMemo(
    () => parseMessageData<EntitySharedData>(message.data),
    [message.data],
  );

  const open = useHubStore((state) => state.open);
  const navigateToView = useHubStore((state) => state.navigateToView);

  const entityId = entityData?.entity_id ?? "";
  const {
    data: entity,
    isError,
    isPending,
  } = useShareableEntity(entityId, undefined, {
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
  });
  const isTakenDown = !!entity?.metadata?.takedown;
  const takedownAlertId = entity?.metadata?.takedown_alert_id as
    | string
    | undefined;
  const isUnavailable = isError || isTakenDown;

  const ownerName = useMemo(() => {
    const fromEntity = entity?.owner ? getPreferredName(entity.owner) : "";
    const fromChannel = findParticipantById(
      options?.context,
      entityData?.owner_id,
    ).name;
    return fromEntity || fromChannel || "Someone";
  }, [entity?.owner, entityData?.owner_id, options?.context]);

  const openInHub = useCallback(() => {
    if (!entityData || isUnavailable) return;
    const viewMap: Partial<Record<EntitySharedData["entity_type"], HubView>> = {
      moment: "view-moment",
      note: "view-note",
      swap: "view-swap",
    };
    const target = viewMap[entityData.entity_type];
    if (!target) return;
    open();
    navigateToView(target, entityData.entity_id);
  }, [entityData, isUnavailable, open, navigateToView]);

  return {
    entityData,
    isPending,
    isUnavailable,
    isTakenDown,
    takedownAlertId,
    ownerName,
    openInHub,
  };
}
